import React, { useCallback, useEffect, useState } from 'react';
import { curatorService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { categoryLabel } from '../../constants/eventCategories';
import { AdminPageHeader, AreaBar, DataTable, shortDate, useAdminShell } from '../../components/admin/shell';

/** The AREA bar's sentence, given the resolved state — spec/sections.md §0.3. */
const areaNote = (a) => {
  if (a.resolving) return 'Resolving area…';
  if (!a.area) return "Search a city to see what's live there.";
  if (!a.region) return 'No area covers that city yet — it gets created the first time something there is featured.';
  return `Live picks for ${a.region.name}.`;
};

/**
 * EVENTS › FEATURED — spec/sections.md §12. The live picks for the AREA's region,
 * in wall order. UNFEATURE is `curation` · edit. FEATURED BY and CLICKS are not on
 * the wall DTO yet, so the table shows POSITION and SINCE (the pick's own stamp).
 */
export default function FeaturedPage() {
  const { can } = useUser();
  const { showAlert } = useDialog();
  const { area: areaCtl } = useAdminShell();
  const area = areaCtl?.area || null;
  const region = areaCtl?.region || null;
  const resolving = !!areaCtl?.resolving;
  const canEdit = can('curation', 'edit');
  const [wall, setWall] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!region?.id) { setWall([]); return; }
    setLoading(true);
    const r = await curatorService.getWall(region.id);
    setLoading(false);
    if (r.success) setWall(r.data?.picks || []);
    else showAlert({ title: 'ERROR', message: r.error || 'Failed to load the wall.', type: 'error', theme: 'admin' });
  }, [region, showAlert]);
  useEffect(() => { load(); }, [load]);

  const unfeature = async (p) => {
    const r = await curatorService.unfeatureEvent(p.eventId);
    if (r.success) load(); else showAlert({ title: 'ERROR', message: r.error || 'Unfeature failed.', type: 'error', theme: 'admin' });
  };

  const empty = !area ? 'SEARCH A CITY ABOVE TO SEE ITS LIVE PICKS.'
    : (resolving ? 'RESOLVING AREA…' : (!region ? 'NO AREA COVERS THAT CITY YET.' : 'NO LIVE PICKS FOR THIS AREA.'));
  const n = wall.length;

  return (
    <>
      <AdminPageHeader section="featured" meta="Live picks for this area." />
      <AreaBar note={areaNote} />
      <div className="flex flex-col gap-4">
        <DataTable
          columns={[
            { key: 'eventTitle', label: 'EVENT', fr: 2.2, render: (p) => p.eventTitle || '—' },
            { key: 'hostName', label: 'HOST', fr: 1.4, render: (p) => p.hostName || '—' },
            { key: 'category', label: 'CATEGORY', fr: 1, render: (p) => categoryLabel(p.category) },
            { key: 'position', label: 'POSITION', fr: 0.9, right: true, render: (p) => (p.position != null ? String(p.position) : '—') },
            { key: 'pickedAt', label: 'SINCE', fr: 0.8, render: (p) => shortDate(p.pickedAt) },
            { key: 'pickedByName', label: 'BY', fr: 0.9, render: (p) => (p.pickedByName ? p.pickedByName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '—') },
            { key: 'clicks', label: 'CLICKS', fr: 0.7, right: true, render: (p) => (p.clicks ?? 0).toLocaleString() },
          ]}
          rows={wall}
          loading={loading || resolving}
          empty={empty}
          hasActions={canEdit}
          actions={(p) => (canEdit ? [{ label: 'UNFEATURE', onClick: () => unfeature(p) }] : [])}
          footerLeft={`${n} live ${n === 1 ? 'pick' : 'picks'} · clicks are taps on the card from the wall, last 30 days`}
        />
      </div>
    </>
  );
}
